import { call, put, takeLatest, delay, select } from 'redux-saga/effects';

import { FleetApi } from 'api';
import { apiCallStatus } from 'helpers/constants';
import { actionTypes } from './constants';
import {
    getFleetBarCharts,
    getFleetStatisticsAssets,
    getFleetStatisticsAssetsGroup,
    getFleetStatisticsPowertrain,
    getFleetAnalyticsAggregatedAssets,
    getFleetAnalyticsAggregatedAssetsGroup,
    getFleetAnalyticsAggregatedPowertrain,
    getFleetAnalyticsAggregatedAssetsAction,
    getFleetAnalyticsAggregatedAssetsGroupAction,
    getFleetAnalyticsAggregatedPowertrainAction,
    updateRefreshCacheFlag
} from './actions';

const getCiamSyncFlag = (state) => state.fleetOverview?.ciamSyncFlag;

// Worker sagas
function* fetchFleetBarCharts({ payload, isLSU, isREC }) {
    try {
        const ciamSyncFlag = yield select(getCiamSyncFlag);
        yield put(getFleetBarCharts(apiCallStatus.LOADING, {}));
        const data = yield call(FleetApi.getFleetsBarCharts, payload);
        yield delay(1000);
        // Combine aggregatedConditions and anomaly data into single object
        const combinedData = {
            aggregatedConditions: data?.aggregatedConditions || [],
            drivesWithCoolingAnomaly: data?.drivesWithCoolingAnomaly || null,
            drivesWithEnergyOutlier: data?.drivesWithEnergyOutlier || null,
            rotatingEquipmentsWithAnomaly: data?.rotatingEquipmentsWithAnomaly || null
        };
        yield put(getFleetBarCharts(apiCallStatus.SUCCESS, combinedData));
        /* SETTING REFRESH CACHE FALSE FOR ALL OTHER APIS AFTER CALLING ABOVE API */
        yield put(updateRefreshCacheFlag(false, ciamSyncFlag));
        yield put(getFleetAnalyticsAggregatedAssetsAction('Assets', payload));
        if (isLSU && !isREC) {
            if (ciamSyncFlag) { /* SETTING REFRESH CACHE TRUE IF USER CLICKS ON SYNC CIAM OR GENERAL FILTER APPLY */
                yield put(updateRefreshCacheFlag(true, ciamSyncFlag));
            }
            yield put(getFleetAnalyticsAggregatedAssetsGroupAction('AssetGroups', payload));
            yield put(getFleetAnalyticsAggregatedPowertrainAction('Powertrains', payload));
        }
    } catch (e) {
        yield put(getFleetBarCharts(apiCallStatus.ERROR, {}, e));
    }
}

function* fetchFleetStatisticsAssets({ payload: { tab, params } }) {
    try {
        yield put(getFleetStatisticsAssets(apiCallStatus.LOADING, []));
        const data = yield call(FleetApi.getFleetStatistics, tab, params);
        yield put(getFleetStatisticsAssets(apiCallStatus.SUCCESS, data || []));
    } catch (e) {
        yield put(getFleetStatisticsAssets(apiCallStatus.ERROR, [], e));
    }
}

function* fetchFleetStatisticsAssetsGroup({ payload: { tab, params } }) {
    try {
        yield put(getFleetStatisticsAssetsGroup(apiCallStatus.LOADING, []));
        const data = yield call(FleetApi.getFleetStatistics, tab, params);
        yield put(getFleetStatisticsAssetsGroup(apiCallStatus.SUCCESS, data || []));
    } catch (e) {
        yield put(getFleetStatisticsAssetsGroup(apiCallStatus.ERROR, [], e));
    }
}

function* fetchFleetStatisticsPowertrain({ payload: { tab, params } }) {
    try {
        yield put(getFleetStatisticsPowertrain(apiCallStatus.LOADING, []));
        const data = yield call(FleetApi.getFleetStatistics, tab, params);
        yield put(getFleetStatisticsPowertrain(apiCallStatus.SUCCESS, data || []));
    } catch (e) {
        yield put(getFleetStatisticsPowertrain(apiCallStatus.ERROR, [], e));
    }
}

function* fetchFleetAnalyticsAggregatedAssets({ payload: { tab, params } }) {
    try {
        yield put(getFleetAnalyticsAggregatedAssets(apiCallStatus.LOADING, []));
        const data = yield call(FleetApi.getFleetAnalyticsAggregated, tab, params);
        yield put(getFleetAnalyticsAggregatedAssets(apiCallStatus.SUCCESS, data || []));
    } catch (e) {
        yield put(getFleetAnalyticsAggregatedAssets(apiCallStatus.ERROR, [], e));
    }
}

function* fetchFleetAnalyticsAggregatedAssetsGroup({ payload: { tab, params } }) {
    try {
        yield put(getFleetAnalyticsAggregatedAssetsGroup(apiCallStatus.LOADING, []));
        const data = yield call(FleetApi.getFleetAnalyticsAggregated, tab, params);
        yield put(getFleetAnalyticsAggregatedAssetsGroup(apiCallStatus.SUCCESS, data?.assetGroups || []));
    } catch (e) {
        yield put(getFleetAnalyticsAggregatedAssetsGroup(apiCallStatus.ERROR, [], e));
    }
}

function* fetchFleetAnalyticsAggregatedPowertrain({ payload: { tab, params } }) {
    try {
        yield put(getFleetAnalyticsAggregatedPowertrain(apiCallStatus.LOADING, []));
        const data = yield call(FleetApi.getFleetAnalyticsAggregated, tab, params);
        yield put(getFleetAnalyticsAggregatedPowertrain(apiCallStatus.SUCCESS, data?.powertrains || []));
    } catch (e) {
        yield put(getFleetAnalyticsAggregatedPowertrain(apiCallStatus.ERROR, [], e));
    }
}

// Watchers (define *_REQUEST types in constants.js)
export function* watchFleetOverviewRootSaga() {
    yield takeLatest(actionTypes.GET_BAR_CHARTS_REQUEST, fetchFleetBarCharts);
    yield takeLatest(actionTypes.GET_STATISTICS_ASSETS_REQUEST, fetchFleetStatisticsAssets);
    yield takeLatest(actionTypes.GET_STATISTICS_ASSETSGROUP_REQUEST, fetchFleetStatisticsAssetsGroup);
    yield takeLatest(actionTypes.GET_STATISTICS_POWERTRAIN_REQUEST, fetchFleetStatisticsPowertrain);
    yield takeLatest(actionTypes.GET_ANALYTICS_AGGREGATED_ASSET_REQUEST, fetchFleetAnalyticsAggregatedAssets);
    yield takeLatest(actionTypes.GET_ANALYTICS_AGGREGATED_ASSETGROUP_REQUEST, fetchFleetAnalyticsAggregatedAssetsGroup);
    yield takeLatest(actionTypes.GET_ANALYTICS_AGGREGATED_POWERTRAIN_REQUEST, fetchFleetAnalyticsAggregatedPowertrain);
}
